import { XIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Badge } from "../Badge";
import { useSelectContext } from "./SelectContext";

/** The selected values of a multi-select, as removable badges (a `disabled` item's badge is locked). Rendered by `Select.Trigger`. */
export const SelectBadgeList = () => {
  const { t } = useTranslation();
  const { selectedValues, labelFor, getItem, removeValue, maxDisplayCount, disabled } = useSelectContext();
  if (selectedValues.length === 0) return null;

  const shownValues = maxDisplayCount === undefined ? selectedValues : selectedValues.slice(0, maxDisplayCount);
  const hiddenCount = selectedValues.length - shownValues.length;
  const accessibleName = (selectedValue: string) => {
    const label = labelFor(selectedValue);
    return getItem(selectedValue)?.textValue || (typeof label === "string" ? label : selectedValue);
  };

  return (
    // `w-0 min-w-full` takes the trigger's width without adding any of its own, so the badges wrap
    // inside the trigger's width instead of widening the field.
    <div data-slot="select-badge-list" className="flex w-0 min-w-full flex-wrap gap-1">
      {shownValues.map((selectedValue) => (
        <Badge
          key={selectedValue}
          variant="soft"
          color="secondary"
          size="sm"
          actionProps={
            disabled || getItem(selectedValue)?.disabled
              ? undefined
              : {
                  icon: XIcon,
                  hoverLabel: t("Remove {{label}}", { label: accessibleName(selectedValue) }),
                  onClick: () => removeValue(selectedValue),
                }
          }
        >
          {labelFor(selectedValue)}
        </Badge>
      ))}
      {hiddenCount > 0 && (
        <Badge variant="outline" color="secondary" size="sm">
          {t("+{{amount}} more", { amount: hiddenCount })}
        </Badge>
      )}
    </div>
  );
};
